import { useCallback, useEffect, useRef, useState } from "react";
import { API, api, formatError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { DatabaseBackup, ShieldAlert, Clock, Download, Trash2, Upload } from "lucide-react";
import { fmtDateTime } from "@/lib/dates";

// Same pattern as every other authenticated file download in the app (AdminPayments.jsx,
// TransactionDetail.jsx, …): a plain `<a target="_blank">` rather than a fetch-as-blob — the
// session cookie carries the request, this just has to be a real navigation, not XHR, for the
// browser to treat it as "save this file" instead of "display this JSON".
const tok = () => localStorage.getItem("dt_token");

// datetime-local <-> ISO. The input has no timezone of its own — it's read/written in whatever
// the browser's local zone is, which is exactly what an admin picking "Saturday 2am" means.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

const Panel = ({ icon: Icon, title, desc, children }) => (
  <div className="bg-[#111623] border border-white/10 rounded-2xl p-6 space-y-4">
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-[#CBA96C] mt-1 shrink-0" strokeWidth={1.5}/>
      <div>
        <h2 className="font-serif-display text-lg text-white">{title}</h2>
        {desc && <p className="text-xs text-[#6B7280] mt-0.5">{desc}</p>}
      </div>
    </div>
    {children}
  </div>
);

// The only names restore will accept — checked here too so a browser's "(1)" suffix on a second
// download is caught before the bytes are sent, not after.
const SNAPSHOT_NAME = /^dharmatn_[0-9TZ]+\.dump$/;

const fmtBytes = (n) => {
  if (n == null) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0, v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export function AdminDisasterRecovery() {
  const [dbName, setDbName] = useState("");
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);

  const [snapshot, setSnapshot] = useState("");
  const [token, setToken] = useState("");
  const [full, setFull] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [restoring, setRestoring] = useState(false);

  const [maint, setMaint] = useState(null);
  const [schedStart, setSchedStart] = useState("");
  const [schedEnd, setSchedEnd] = useState("");
  const [schedNote, setSchedNote] = useState("");
  const [maintBusy, setMaintBusy] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    api.get("/admin/db/backups")
      .then(r => { setDbName(r.data.db_name || ""); setBackups(r.data.backups || []); })
      .catch(() => toast.error("Could not load the list of backups"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const refreshMaintenance = useCallback(() => {
    api.get("/admin/maintenance").then(r => {
      setMaint(r.data);
      setSchedStart(toLocalInput(r.data.start_at));
      setSchedEnd(toLocalInput(r.data.end_at));
      setSchedNote(r.data.note || "");
    }).catch(() => toast.error("Could not load the maintenance window"));
  }, []);
  useEffect(() => { refreshMaintenance(); }, [refreshMaintenance]);

  const maintAction = async (patch, okMsg) => {
    setMaintBusy(true);
    try {
      const r = await api.put("/admin/maintenance", patch);
      setMaint(r.data);
      toast.success(okMsg);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not update the maintenance window");
    } finally {
      setMaintBusy(false);
    }
  };
  const saveSchedule = () => {
    if (!schedStart || !schedEnd) { toast.error("Set both a start and an end time"); return; }
    maintAction({
      start_at: fromLocalInput(schedStart), end_at: fromLocalInput(schedEnd),
      note: schedNote, override: null,
    }, "Window scheduled");
  };

  const backupNow = async () => {
    setBackingUp(true);
    try {
      const r = await api.post("/admin/db/backup");
      toast.success(`Backup taken: ${r.data.filename} (${fmtBytes(r.data.size_bytes)})`);
      refresh();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Backup failed");
    } finally {
      setBackingUp(false);
    }
  };

  // Putting back a snapshot downloaded earlier — the server's own copy deleted or rotated away.
  // It only joins the list: restoring it still needs the window and the SSH-minted token.
  const fileInput = useRef(null);
  const [uploadPct, setUploadPct] = useState(null); // 0-100 while sending, null otherwise
  const uploadSnapshot = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // so picking the same file again after a refusal still fires onChange
    if (!file) return;
    if (!SNAPSHOT_NAME.test(file.name)) {
      toast.error(`"${file.name}" isn't a snapshot name. Rename it back to the name it was downloaded with (dharmatn_<timestamp>.dump).`);
      return;
    }
    setUploadPct(0);
    try {
      const r = await api.post("/admin/db/backups/upload", file, {
        params: { filename: file.name },
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (p) => p.total && setUploadPct(Math.round((p.loaded / p.total) * 100)),
      });
      toast.success(`Uploaded ${r.data.filename} (${fmtBytes(r.data.size_bytes)}) — it can now be restored below.`);
      refresh();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadPct(null);
    }
  };

  const [deleting, setDeleting] = useState(null); // filename mid-delete, or null
  const [picked, setPicked] = useState({});       // filename -> true, for the bulk delete
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Derived from the live list rather than kept in step with it: a tick left behind by a snapshot
  // that has since been rotated away or deleted in another tab simply stops counting, so the
  // button can never act on a filename that is no longer there.
  const pickedFiles = backups.filter(b => picked[b.filename]).map(b => b.filename);
  const allPicked = backups.length > 0 && pickedFiles.length === backups.length;
  const togglePicked = (filename) => setPicked(p => ({ ...p, [filename]: !p[filename] }));
  const togglePickAll = () =>
    setPicked(allPicked ? {} : Object.fromEntries(backups.map(b => [b.filename, true])));

  const deleteSelected = async () => {
    // One DELETE per file against the existing per-snapshot route, rather than a new bulk
    // endpoint. Removing files from disk is not transactional, so a server-side bulk delete would
    // run this same loop with the same partial-failure outcome — and doing it here means every
    // deletion still passes `_backup_path`'s filename check and writes its own audit line, with no
    // new route to guard. Sequential, so a failure is attributable to a named file and the box is
    // not handed N concurrent unlinks.
    const files = pickedFiles;
    if (!files.length) return;
    // Clearing every snapshot leaves no recovery point at all, which is worth saying out loud on
    // this page of all pages. Not refused — the single-snapshot button has never refused it, and
    // an admin may be deliberately emptying a box — but not slipped past them either.
    const warning = allPicked
      ? "\n\nThis is every snapshot you have. You will have no recovery point until the next backup runs."
      : "";
    if (!window.confirm(
      `Delete ${files.length} snapshot${files.length > 1 ? "s" : ""}? This can't be undone.${warning}`)) return;
    setBulkDeleting(true);
    const failed = [];
    for (const filename of files) {
      try {
        await api.delete(`/admin/db/backups/${filename}`);
        if (snapshot === filename) setSnapshot("");
      } catch {
        failed.push(filename);
      }
    }
    setBulkDeleting(false);
    setPicked({});
    refresh();
    const done = files.length - failed.length;
    if (failed.length) toast.error(`Deleted ${done} of ${files.length}. Failed: ${failed.join(", ")}`);
    else toast.success(`Deleted ${done} snapshot${done > 1 ? "s" : ""}`);
  };

  const deleteBackup = async (filename) => {
    // A backup isn't live data — deleting one only removes a recovery point, not something a
    // restore/maintenance-window dance is warranted for. A plain confirm is the proportionate
    // amount of friction here, not the CLI-token flow restore uses.
    if (!window.confirm(`Delete ${filename}? This can't be undone.`)) return;
    setDeleting(filename);
    try {
      await api.delete(`/admin/db/backups/${filename}`);
      toast.success(`Deleted ${filename}`);
      refresh();
      if (snapshot === filename) setSnapshot("");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const restore = async () => {
    setRestoring(true);
    try {
      await api.post("/admin/db/restore", {
        snapshot, token, confirm_db_name: confirmName, full,
      });
      toast.success("Restore complete.");
      setToken(""); setConfirmName(""); setFull(false);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const canRestore = maint?.active && snapshot && token.trim() && confirmName === dbName && !restoring;

  return (
    <div className="space-y-6" data-testid="admin-disaster-recovery-page">
      <h1 className="font-serif-display text-3xl md:text-4xl text-white">Disaster Recovery</h1>

      <Panel icon={DatabaseBackup} title="Backups"
             desc="Compressed, verified on write, and rotated automatically — the same script runs whether it's triggered here, by cron, or by hand over SSH.">
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="dr-backup-now" onClick={backupNow} disabled={backingUp}>
            {backingUp ? "Backing up…" : "Back up now"}
          </Button>
          <input ref={fileInput} type="file" accept=".dump" className="hidden"
                 onChange={uploadSnapshot} data-testid="dr-backup-upload-input"/>
          <Button data-testid="dr-backup-upload" variant="secondary" disabled={uploadPct !== null}
                  onClick={() => fileInput.current?.click()}
                  title="Put back a snapshot you downloaded from this page earlier">
            <Upload className="w-3.5 h-3.5 mr-1.5"/>
            {uploadPct !== null ? `Uploading… ${uploadPct}%` : "Upload snapshot"}
          </Button>
          {/* Appears only once something is ticked, so the default state of this page carries no
              bulk-destructive button at all. */}
          {pickedFiles.length > 0 && (
            <Button data-testid="dr-backup-delete-selected" onClick={deleteSelected} disabled={bulkDeleting}
                    className="bg-transparent border border-[#F27373]/40 text-[#F27373] hover:bg-[#F27373]/10 hover:text-[#F27373]">
              <Trash2 className="w-3.5 h-3.5 mr-1.5"/>
              {bulkDeleting ? `Deleting ${pickedFiles.length}…` : `Delete ${pickedFiles.length} selected`}
            </Button>
          )}
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-10">
              <Checkbox data-testid="dr-backup-select-all" checked={allPicked} onCheckedChange={togglePickAll}
                        disabled={backups.length === 0 || bulkDeleting} aria-label="Select all snapshots"/>
            </TableHead>
            <TableHead>Snapshot</TableHead><TableHead>Size</TableHead><TableHead>Taken</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {backups.map(b => (
              <TableRow key={b.filename} data-state={picked[b.filename] ? "selected" : undefined}>
                <TableCell>
                  <Checkbox data-testid={`dr-backup-pick-${b.filename}`} checked={!!picked[b.filename]}
                            onCheckedChange={() => togglePicked(b.filename)} disabled={bulkDeleting}
                            aria-label={`Select ${b.filename}`}/>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {b.filename}
                  {/* Not taken on this server — the operator minting a restore token should know. */}
                  {b.uploaded && (
                    <span className="ml-2 font-sans text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border border-[#F2A65A]/40 text-[#F2A65A]"
                          title={`Uploaded by ${b.uploaded.by || "an admin"} on ${fmtDateTime(b.uploaded.at)}`}
                          data-testid={`dr-backup-uploaded-${b.filename}`}>uploaded</span>
                  )}
                </TableCell>
                <TableCell className="text-xs">{fmtBytes(b.size_bytes)}</TableCell>
                <TableCell className="text-xs">{fmtDateTime(b.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <a href={`${API}/admin/db/backups/${b.filename}/download?_t=${tok()}`}
                       target="_blank" rel="noopener noreferrer" title="Download" aria-label={`Download ${b.filename}`}
                       data-testid={`dr-backup-download-${b.filename}`}
                       className="p-1.5 rounded-md text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors">
                      <Download className="w-3.5 h-3.5"/>
                    </a>
                    <button onClick={() => deleteBackup(b.filename)} disabled={deleting === b.filename || bulkDeleting}
                            title="Delete" aria-label={`Delete ${b.filename}`}
                            data-testid={`dr-backup-delete-${b.filename}`}
                            className="p-1.5 rounded-md text-[#A0AABF] hover:text-[#F27373] hover:bg-[#F27373]/10 transition-colors disabled:opacity-40">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && backups.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-[#6B7280] py-10">No snapshots yet — take one above, upload one you downloaded earlier, or wait for the nightly backup.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Panel>

      <Panel icon={Clock} title="Maintenance Window"
             desc="Restore, and a deploy that carries a migration, only run while a window is open — customer and advocate traffic is locked out for its duration; admin is never affected.">
        <div className={`text-sm rounded-lg px-3 py-2 border ${maint?.active ? "bg-[#F2A65A]/10 border-[#F2A65A]/30 text-[#F2A65A]" : "bg-[#080C16] border-white/10 text-[#A0AABF]"}`} data-testid="dr-maintenance-status">
          {maint?.active ? "ACTIVE — customer/advocate traffic is locked out right now" : "Inactive — customer/advocate traffic is unaffected"}
          {maint?.override && <span className="opacity-70"> (manual override: {maint.override})</span>}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Starts</Label>
            <Input data-testid="dr-maint-start" type="datetime-local" value={schedStart}
                   onChange={e => setSchedStart(e.target.value)} className="bg-[#080C16] border-white/10 text-white"/>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Ends</Label>
            <Input data-testid="dr-maint-end" type="datetime-local" value={schedEnd}
                   onChange={e => setSchedEnd(e.target.value)} className="bg-[#080C16] border-white/10 text-white"/>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Note shown to customers/advocates</Label>
          <Input data-testid="dr-maint-note" value={schedNote} onChange={e => setSchedNote(e.target.value)}
                 placeholder="We're upgrading the system and will be back shortly." className="bg-[#080C16] border-white/10 text-white"/>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="dr-maint-schedule" onClick={saveSchedule} disabled={maintBusy}>Save schedule</Button>
          <Button data-testid="dr-maint-start-now" variant="secondary" disabled={maintBusy}
                  onClick={() => maintAction({ override: "on" }, "Maintenance started now")}>Start now</Button>
          <Button data-testid="dr-maint-end-now" variant="secondary" disabled={maintBusy}
                  onClick={() => maintAction({ override: "off" }, "Maintenance ended now")}>End now</Button>
          <Button data-testid="dr-maint-clear" variant="ghost" disabled={maintBusy}
                  onClick={() => maintAction({ override: null }, "Override cleared")}>Clear override</Button>
        </div>
      </Panel>

      <Panel icon={ShieldAlert} title="Restore"
             desc="Restoring is triggered here but authorized on the server: an operator with SSH access to the box must mint a one-time token before this can do anything. A browser session or an admin password alone is never enough.">
        {!maint?.active && (
          <div className="text-xs text-[#F2A65A] bg-[#F2A65A]/10 border border-[#F2A65A]/30 rounded-lg p-3" data-testid="dr-restore-needs-window">
            Restore requires an active maintenance window — start or schedule one above first.
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Snapshot to restore</Label>
          <Select value={snapshot} onValueChange={setSnapshot}>
            <SelectTrigger data-testid="dr-restore-snapshot" className="bg-[#080C16] border-white/10 text-white"><SelectValue placeholder="Choose a snapshot"/></SelectTrigger>
            <SelectContent>{backups.map(b => <SelectItem key={b.filename} value={b.filename}>{b.filename} ({fmtBytes(b.size_bytes)}){b.uploaded ? " — uploaded" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {snapshot && (
          <div className="text-xs text-[#A0AABF] bg-[#080C16] border border-white/10 rounded-lg p-3 space-y-1">
            <div>Run this over SSH on the server, then paste the token it prints below:</div>
            <code className="block text-[#CBA96C] select-all">docker compose exec backend deploy/restore-authorize.sh {snapshot}</code>
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Authorization token</Label>
          <Input data-testid="dr-restore-token" value={token} onChange={e => setToken(e.target.value)}
                 placeholder="Pasted from restore-authorize.sh" className="bg-[#080C16] border-white/10 text-white font-mono text-xs"/>
        </div>

        <label className="flex items-start gap-2 text-xs text-[#A0AABF]">
          <Checkbox data-testid="dr-restore-full" checked={full} onCheckedChange={setFull} className="mt-0.5"/>
          <span>Full restore — also overwrites today's live document templates and site settings.
            Only for rebuilding after total data loss onto an empty database; leave unchecked to
            recover business activity while protecting what's live now.</span>
        </label>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">
            Type the database name to confirm{dbName ? ` (${dbName})` : ""}
          </Label>
          <Input data-testid="dr-restore-confirm" value={confirmName} onChange={e => setConfirmName(e.target.value)}
                 className="bg-[#080C16] border-white/10 text-white"/>
        </div>

        <Button data-testid="dr-restore-submit" variant="destructive" onClick={restore} disabled={!canRestore}>
          {restoring ? "Restoring…" : "Restore"}
        </Button>
      </Panel>
    </div>
  );
}
